import {
  markdownToPreview,
  markdownToPreviewInline,
} from '@components/designer/utils/markdownToPreview';
import type { KnowledgeArticleDTO } from '@conversed/rest/src/contracts/article-architect/article-architect.dto';
import type { KnowledgeArticleStatus } from '@conversed/rest/src/contracts/article-architect/article-architect.enums';
import { purify } from '@helpers/domHelper';
import parse from 'html-react-parser';

export const parseArticleMarkdown = (body: string): ReturnType<typeof parse> =>
  parse(purify(markdownToPreview(body), 'markdown-link-inherit'));

// For short snippets rendered inside an existing paragraph - the conflict judge's
// evidence quotes. The block renderer would emit a <p>, which cannot legally nest
// inside the quote's own <p>.
//
// The <span> wrapper is load-bearing: `purify` runs DOMPurify with FORCE_BODY: false,
// which silently drops a leading bare text node ("valid for **24h**" would render as
// just "24h"). Block-rendered markdown always starts with a tag, so this never
// surfaced there. Wrapping gives the sanitiser an element to start from.
export const parseArticleMarkdownInline = (
  text: string,
): ReturnType<typeof parse> =>
  parse(
    purify(
      `<span>${markdownToPreviewInline(text)}</span>`,
      'markdown-link-inherit',
    ),
  );

export const filterArticlesByStatus = (
  articles: KnowledgeArticleDTO[],
  status: KnowledgeArticleStatus,
): KnowledgeArticleDTO[] =>
  articles.filter((article) => article.status === status);

// Counts conflict pairs the way the conflict resolution page does: a pair is
// recorded on both articles, so (A,X) and (X,A) count once, and a conflict with an
// article that no longer exists is not shown there, so it is not counted here.
export const countUniqueConflictPairs = (
  articles: KnowledgeArticleDTO[],
): number => {
  const articleIds = new Set(articles.map((article) => article._id.toString()));
  const seenPairs = new Set<string>();

  for (const article of articles) {
    const articleId = article._id.toString();
    for (const conflict of article.conflicts ?? []) {
      if (!articleIds.has(conflict.articleId)) {
        continue;
      }
      seenPairs.add([articleId, conflict.articleId].sort().join('-'));
    }
  }

  return seenPairs.size;
};
